/**
 * Signalbox → SignalBox Preferences. Admin only.
 *
 * The three things an admin maintains, none of which a reader ever reaches:
 *
 *   TASKS        the recurring task list — add, edit, delete, and set what each
 *                task is doing (open / paused / closed). This is the ONLY place
 *                the list is changed; the board is for doing the work.
 *   PERIODS      which months, quarters and years exist, and when each opens.
 *   PROGRAMMES   the dated runs of work — the budget round — their milestone
 *                plan, and each cycle's own dates.
 *
 * People are not here, deliberately: they come from Admin → Users, so there is
 * only ever one list of people in the app to maintain.
 *
 * Editing lives HERE and not on the board on purpose. The board is where the work
 * is done — marking complete and adding notes, which a reader may do. Changing the
 * task list is an admin job and stays behind Preferences.
 */
import { useState } from 'react'
import * as sb from '../../signalbox/api'
import { CADENCES, type Board, type Cadence, type ProgrammeOut } from '../../signalbox/api'
import TasksSection from './TasksSection'
import { MONTH_NAMES, day, fmtDate, nameFor } from '../../signalbox/status'

type Section = 'tasks' | 'periods' | 'programmes'

interface SectionProps {
  board: Board
  busy: boolean
  act: (fn: () => Promise<unknown>) => Promise<boolean>
  today: Date
}

export default function Preferences({ board, busy, act, today }: SectionProps) {
  const [section, setSection] = useState<Section>('tasks')

  return (
    <>
      <div className="pill-stack">
        <div className="pill-row">
          <span className="pill-row__label">Preferences</span>
          {(
            [
              ['tasks', 'Tasks'],
              ['periods', 'Periods'],
              ['programmes', 'Programmes'],
            ] as Array<[Section, string]>
          ).map(([id, label]) => (
            <button
              key={id}
              className={'month-pill' + (section === id ? ' active' : '')}
              onClick={() => setSection(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {section === 'tasks' && <TasksSection board={board} busy={busy} act={act} today={today} />}
      {section === 'periods' && (
        <PeriodsSection board={board} busy={busy} act={act} today={today} />
      )}
      {section === 'programmes' && <ProgrammesSection board={board} busy={busy} act={act} />}
    </>
  )
}

/** Somebody's level in the app, shown wherever work is assigned to them. */
const RolePill = ({ board, upn }: { board: Board; upn: string }) => {
  const p = board.people.find((x) => x.upn === upn)
  return (
    <span className="role-pill" data-role={p?.role ?? 'none'}>
      {p?.role ?? 'no access'}
    </span>
  )
}

// ─── Periods ─────────────────────────────────────────────────────────────────

function PeriodsSection({ board, busy, act, today }: SectionProps) {
  const [cadence, setCadence] = useState<Cadence>('monthly')
  const periods = board.periods[cadence]

  return (
    <>
      <div className="pill-stack">
        <div className="pill-row">
          <span className="pill-row__label">Cadence</span>
          {CADENCES.map(([id, label]) => (
            <button
              key={id}
              className={'month-pill' + (cadence === id ? ' active' : '')}
              onClick={() => setCadence(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <p className="tm-scope">
        Periods open on a schedule, so the board shows what is actually in play rather than every
        month to the end of the year. Monthly periods open two months before they start, quarterly on
        the first day of the quarter, annual on 1 January. Nothing has to run for that to happen — a
        period is open once its opening date has passed. Signalbox generates nothing ending before{' '}
        <strong>{fmtDate(day(board.liveFrom))}</strong>.
      </p>

      <div className="table-wrap tm-stickyhead">
        <table className="table tm-def-table">
          <thead>
            <tr>
              <th>Period</th>
              <th>Ends</th>
              <th>Opens</th>
              <th>Status</th>
              <th>Signed off</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {periods.map((p) => (
              <tr key={p.id}>
                <td className="tm-def-name">
                  <span className="strong">{p.long}</span>
                  {p.openedEarly && <span className="tm-task-ref">opened early</span>}
                </td>
                <td className="mono">{fmtDate(day(p.end))}</td>
                <td className="mono">{fmtDate(day(p.openFrom))}</td>
                <td>
                  {p.open ? (
                    <span className="status-tag status-tag--complete">Open</span>
                  ) : (
                    <span className="status-tag status-tag--future">Not open yet</span>
                  )}
                </td>
                <td>{p.completions > 0 ? `${p.completions} task${p.completions === 1 ? '' : 's'}` : <span className="muted">—</span>}</td>
                <td className="row-actions">
                  {!p.open && (
                    <button
                      className="btn btn--small"
                      disabled={busy}
                      onClick={() => act(() => sb.openPeriod(p.id))}
                    >
                      Open now
                    </button>
                  )}
                  {/* Deliberately hard to remove: only a period that was opened
                      EARLY and holds no completed work. And removing it does NOT
                      cancel the schedule — it returns on its own date. */}
                  {p.open && p.openedEarly && p.completions === 0 && (
                    <button
                      className="btn btn--small tm-danger"
                      disabled={busy}
                      onClick={() =>
                        confirm(
                          `Remove ${p.long}?\n\nThis only forgets that it was opened early. It will open ` +
                            `by itself on ${fmtDate(day(p.openFrom))} as normal.`
                        ) && act(() => sb.forgetPeriod(p.id))
                      }
                    >
                      Remove
                    </button>
                  )}
                  {p.open && p.openedEarly && p.completions > 0 && (
                    <span className="muted">has completed work</span>
                  )}
                  {p.open && !p.openedEarly && day(p.end) < today && p.completions > 0 && (
                    <button
                      className="btn btn--small"
                      disabled={busy}
                      onClick={() =>
                        confirm(
                          `Reopen ${p.long}? Every completion stamp in the period is cleared.`
                        ) && act(() => sb.reopenPeriod(p.id))
                      }
                    >
                      ↺ Reopen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ─── Programmes ──────────────────────────────────────────────────────────────

interface ProgForm {
  name: string
  blurb: string
  repeatKind: string
  naming: string
  startDay: string
  startMonth: string
  firstFy: string
}

interface MilestoneForm {
  name: string
  assigneeUpn: string
  day: string
  month: string
  ref: string
  instructions: string
}

function ProgrammesSection({
  board,
  busy,
  act,
}: {
  board: Board
  busy: boolean
  act: (fn: () => Promise<unknown>) => Promise<boolean>
}) {
  const [selected, setSelected] = useState<number | 'new' | null>(
    board.programmes[0]?.id ?? null
  )
  const [progForm, setProgForm] = useState<ProgForm | null>(null)
  const [msForm, setMsForm] = useState<{ id: number | 'new'; f: MilestoneForm } | null>(null)
  const [dateEdit, setDateEdit] = useState<{ fy: number; dates: Record<number, string> } | null>(null)
  const [formErr, setFormErr] = useState<string | null>(null)

  const prog = typeof selected === 'number' ? board.programmes.find((p) => p.id === selected) : null

  const startProg = (p: ProgrammeOut | null) => {
    setFormErr(null)
    setMsForm(null)
    setDateEdit(null)
    if (!p) {
      setProgForm({
        name: '',
        blurb: '',
        repeatKind: 'annual',
        naming: 'runs',
        startDay: '1',
        startMonth: '7',
        firstFy: String(new Date().getFullYear()),
      })
      setSelected('new')
      return
    }
    setProgForm({
      name: p.name,
      blurb: p.blurb ?? '',
      repeatKind: p.repeatKind,
      naming: p.naming,
      startDay: String(p.startDay),
      startMonth: String(p.startMonth),
      firstFy: String(p.firstFy),
    })
    setSelected(p.id)
  }

  async function saveProg() {
    if (!progForm) return
    if (!progForm.name.trim()) return setFormErr('A programme needs a name.')
    const payload: sb.ProgrammeInput = {
      name: progForm.name.trim(),
      blurb: progForm.blurb.trim() || null,
      repeatKind: progForm.repeatKind,
      naming: progForm.naming,
      startDay: Number(progForm.startDay),
      startMonth: Number(progForm.startMonth),
      firstFy: Number(progForm.firstFy),
    }
    if (selected === 'new') {
      if (await act(() => sb.createProgramme(payload))) {
        setProgForm(null)
        setSelected(null)
      }
    } else if (typeof selected === 'number') {
      if (await act(() => sb.updateProgramme(selected, payload))) setProgForm(null)
    }
  }

  async function saveMilestone() {
    if (!msForm || !prog) return
    if (!msForm.f.name.trim()) return setFormErr('A milestone needs a name.')
    const payload: sb.MilestoneInput = {
      name: msForm.f.name.trim(),
      assigneeUpn: msForm.f.assigneeUpn || null,
      day: Number(msForm.f.day),
      month: Number(msForm.f.month),
      ref: msForm.f.ref.trim() || null,
      instructions: msForm.f.instructions.trim() || null,
    }
    const done =
      msForm.id === 'new'
        ? await act(() => sb.createMilestone(prog.id, payload))
        : await act(() => sb.updateMilestone(prog.id, msForm.id as number, payload))
    if (done) setMsForm(null)
  }

  return (
    <>
      <div className="tm-admin-bar">
        <div className="pill-row">
          <span className="pill-row__label">Programme</span>
          {board.programmes.map((p) => (
            <button
              key={p.id}
              className={'month-pill' + (selected === p.id ? ' active' : '')}
              onClick={() => {
                setSelected(p.id)
                setProgForm(null)
                setMsForm(null)
                setDateEdit(null)
              }}
            >
              {p.name}
              {p.finished && <span className="muted"> · finished</span>}
            </button>
          ))}
        </div>
        <button className="btn btn--primary btn--small" onClick={() => startProg(null)}>
          + Add programme
        </button>
      </div>

      {formErr && <p className="tm-err">{formErr}</p>}

      {(selected === 'new' || progForm) && progForm && (
        <div className="tm-editor">
          <h4>{selected === 'new' ? 'New programme' : `Edit ${prog?.name ?? ''}`}</h4>
          <div className="tm-form">
            <div className="tm-field tm-field--wide">
              <label>Name</label>
              <input
                className="sched-input"
                value={progForm.name}
                onChange={(e) => setProgForm({ ...progForm, name: e.target.value })}
                placeholder="e.g. Budgets"
              />
            </div>
            <div className="tm-field tm-field--wide">
              <label>What it is</label>
              <input
                className="sched-input"
                value={progForm.blurb}
                onChange={(e) => setProgForm({ ...progForm, blurb: e.target.value })}
                placeholder="e.g. Setting next year's budget, August to January."
              />
            </div>
            <div className="tm-field">
              <label>Repeats</label>
              <select
                className="sched-input"
                value={progForm.repeatKind}
                onChange={(e) => setProgForm({ ...progForm, repeatKind: e.target.value })}
              >
                <option value="annual">every year</option>
                <option value="oneoff">once — a one-off</option>
              </select>
              <span className="tm-hint">
                A one-off has no cycles at all: one run, so there is no next one to open and no year
                to name it after. It drops off the tab bar once every milestone is complete.
              </span>
            </div>
            {progForm.repeatKind === 'annual' && (
              <>
                <div className="tm-field">
                  <label>Starts</label>
                  <div className="tm-due-row">
                    <input
                      className="sched-input sched-input--num"
                      type="number"
                      min={1}
                      max={31}
                      value={progForm.startDay}
                      onChange={(e) => setProgForm({ ...progForm, startDay: e.target.value })}
                    />
                    <select
                      className="sched-input"
                      value={progForm.startMonth}
                      onChange={(e) => setProgForm({ ...progForm, startMonth: e.target.value })}
                    >
                      {MONTH_NAMES.map((m, i) => (
                        <option key={m} value={i}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="tm-hint">
                    A cycle opens on its start date, the same rule as a period, and can be opened
                    early.
                  </span>
                </div>
                <div className="tm-field">
                  <label>Named for</label>
                  <select
                    className="sched-input"
                    value={progForm.naming}
                    onChange={(e) => setProgForm({ ...progForm, naming: e.target.value })}
                  >
                    <option value="runs">the year it runs in</option>
                    <option value="next">the year ahead</option>
                  </select>
                  <span className="tm-hint">
                    A budget round running from August 2026 sets the FY27-28 budget, so it is named
                    for the year ahead. Naming shifts the label only — the dates and the schedule
                    still key off the year it runs in.
                  </span>
                </div>
                <div className="tm-field">
                  <label>First cycle</label>
                  <input
                    className="sched-input sched-input--num"
                    type="number"
                    value={progForm.firstFy}
                    onChange={(e) => setProgForm({ ...progForm, firstFy: e.target.value })}
                  />
                </div>
              </>
            )}
          </div>
          <div className="tm-editor__actions">
            <button className="btn btn--primary btn--small" disabled={busy} onClick={saveProg}>
              {selected === 'new' ? 'Add programme' : 'Save changes'}
            </button>
            <button className="btn btn--small" onClick={() => setProgForm(null)}>
              Cancel
            </button>
            <span className="tm-spacer" />
            {prog && (
              <button
                className="btn btn--small tm-danger"
                disabled={busy}
                onClick={() =>
                  confirm(`Delete "${prog.name}" and all its milestones?`) &&
                  act(() => sb.deleteProgramme(prog.id)).then((gone) => {
                    if (gone) {
                      setProgForm(null)
                      setSelected(null)
                    }
                  })
                }
              >
                Delete programme
              </button>
            )}
          </div>
        </div>
      )}

      {prog && !progForm && (
        <>
          <div className="tm-period-head">
            <div>
              <h3>{prog.name}</h3>
              <p className="muted">{prog.blurb}</p>
            </div>
            <button className="btn btn--small" onClick={() => startProg(prog)}>
              Edit programme
            </button>
          </div>

          <div className="tm-admin-bar">
            <p className="muted" style={{ margin: 0 }}>
              {prog.tasks.length} milestone{prog.tasks.length === 1 ? '' : 's'}. These dates are the{' '}
              <strong>plan</strong>; each cycle carries its own.
            </p>
            {!msForm && (
              <button
                className="btn btn--primary btn--small"
                onClick={() =>
                  setMsForm({
                    id: 'new',
                    f: {
                      name: '',
                      assigneeUpn: '',
                      day: '1',
                      month: String(prog.startMonth),
                      ref: '',
                      instructions: '',
                    },
                  })
                }
              >
                + Add milestone
              </button>
            )}
          </div>

          {msForm && (
            <div className="tm-editor">
              <h4>{msForm.id === 'new' ? 'New milestone' : 'Edit milestone'}</h4>
              <div className="tm-form">
                <div className="tm-field tm-field--wide">
                  <label>Milestone</label>
                  <input
                    className="sched-input"
                    value={msForm.f.name}
                    onChange={(e) => setMsForm({ ...msForm, f: { ...msForm.f, name: e.target.value } })}
                  />
                </div>
                <div className="tm-field">
                  <label>Assigned to</label>
                  <select
                    className="sched-input"
                    value={msForm.f.assigneeUpn}
                    onChange={(e) =>
                      setMsForm({ ...msForm, f: { ...msForm.f, assigneeUpn: e.target.value } })
                    }
                  >
                    <option value="">Unassigned</option>
                    {board.people.map((p) => (
                      <option key={p.upn} value={p.upn}>
                        {p.displayName ?? p.upn} ({p.role})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="tm-field">
                  <label>Planned date</label>
                  <div className="tm-due-row">
                    <input
                      className="sched-input sched-input--num"
                      type="number"
                      min={1}
                      max={31}
                      value={msForm.f.day}
                      onChange={(e) => setMsForm({ ...msForm, f: { ...msForm.f, day: e.target.value } })}
                    />
                    <select
                      className="sched-input"
                      value={msForm.f.month}
                      onChange={(e) =>
                        setMsForm({ ...msForm, f: { ...msForm.f, month: e.target.value } })
                      }
                    >
                      {MONTH_NAMES.map((m, i) => (
                        <option key={m} value={i}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="tm-hint">
                    Resolved against the cycle: months at or after {MONTH_NAMES[prog.startMonth]} fall
                    in the cycle year, earlier months in the year after — a programme runs across the
                    year end.
                  </span>
                </div>
                <div className="tm-field">
                  <label>Reference</label>
                  <input
                    className="sched-input"
                    value={msForm.f.ref}
                    onChange={(e) => setMsForm({ ...msForm, f: { ...msForm.f, ref: e.target.value } })}
                  />
                </div>
                <div className="tm-field tm-field--wide">
                  <label>Standing notes</label>
                  <textarea
                    className="tm-notebox"
                    value={msForm.f.instructions}
                    onChange={(e) =>
                      setMsForm({ ...msForm, f: { ...msForm.f, instructions: e.target.value } })
                    }
                  />
                </div>
              </div>
              <div className="tm-editor__actions">
                <button className="btn btn--primary btn--small" disabled={busy} onClick={saveMilestone}>
                  {msForm.id === 'new' ? 'Add milestone' : 'Save changes'}
                </button>
                <button className="btn btn--small" onClick={() => setMsForm(null)}>
                  Cancel
                </button>
                <span className="tm-spacer" />
                {msForm.id !== 'new' && (
                  <button
                    className="btn btn--small tm-danger"
                    disabled={busy}
                    onClick={() =>
                      confirm('Delete this milestone?') &&
                      act(() => sb.deleteMilestone(prog.id, msForm.id as number)).then((gone) => {
                        if (gone) setMsForm(null)
                      })
                    }
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="table-wrap">
            <table className="table tm-def-table">
              <thead>
                <tr>
                  <th>Milestone</th>
                  <th>Assigned to</th>
                  <th>Planned</th>
                  <th>Reference</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {prog.tasks.map((t) => (
                  <tr key={t.id}>
                    <td className="tm-def-name">
                      <span className="strong">{t.name}</span>
                    </td>
                    <td>
                      {t.assigneeUpn ? (
                        <>
                          {nameFor(board.people, t.assigneeUpn)}{' '}
                          <RolePill board={board} upn={t.assigneeUpn} />
                        </>
                      ) : (
                        <span className="muted">Unassigned</span>
                      )}
                    </td>
                    <td className="mono">
                      {t.day} {MONTH_NAMES[t.month]}
                    </td>
                    <td>{t.ref ?? <span className="muted">—</span>}</td>
                    <td className="row-actions">
                      <button
                        className="btn btn--small"
                        onClick={() =>
                          setMsForm({
                            id: t.id,
                            f: {
                              name: t.name,
                              assigneeUpn: t.assigneeUpn ?? '',
                              day: String(t.day),
                              month: String(t.month),
                              ref: t.ref ?? '',
                              instructions: t.instructions ?? '',
                            },
                          })
                        }
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {prog.repeatKind === 'annual' && (
            <>
              <h4 style={{ marginTop: '1.5rem' }}>Cycles</h4>
              <p className="tm-scope">
                A cycle carries its own dates, seeded from the plan above. Only the{' '}
                <strong>difference</strong> is stored, so setting a date back to the planned one makes
                the milestone track the plan again — and changing the plan never rewrites a cycle that
                has already run. A milestone that is already signed off cannot be re-dated; reopen it
                first.
              </p>
              <div className="table-wrap">
                <table className="table tm-def-table">
                  <thead>
                    <tr>
                      <th>Cycle</th>
                      <th>Runs in</th>
                      <th>Starts</th>
                      <th>Status</th>
                      <th>Moved dates</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {prog.cycles.map((c) => (
                      <>
                        <tr key={c.fy}>
                          <td className="tm-def-name">
                            <span className="strong">{c.label}</span>
                            {c.openedEarly && <span className="tm-task-ref">opened early</span>}
                          </td>
                          <td>{c.runsIn}</td>
                          <td className="mono">{fmtDate(day(c.start))}</td>
                          <td>
                            {c.open ? (
                              <span className="status-tag status-tag--complete">Open</span>
                            ) : (
                              <span className="status-tag status-tag--future">Not open yet</span>
                            )}
                          </td>
                          <td>
                            {c.overrides > 0 ? (
                              <strong>{c.overrides}</strong>
                            ) : (
                              <span className="muted">follows the plan</span>
                            )}
                          </td>
                          <td className="row-actions">
                            {!c.open && (
                              <button
                                className="btn btn--small"
                                disabled={busy}
                                onClick={() => act(() => sb.openCycle(prog.id, c.fy))}
                              >
                                Open now
                              </button>
                            )}
                            <button
                              className="btn btn--small"
                              onClick={() =>
                                setDateEdit(
                                  dateEdit?.fy === c.fy
                                    ? null
                                    : {
                                        fy: c.fy,
                                        dates: Object.fromEntries(
                                          c.milestones.map((m) => [m.programmeTaskId, m.due])
                                        ),
                                      }
                                )
                              }
                            >
                              Dates
                            </button>
                          </td>
                        </tr>
                        {dateEdit?.fy === c.fy && (
                          <tr key={`${c.fy}-dates`}>
                            <td colSpan={6} className="tm-editor">
                              <h4>{c.label} cycle dates</h4>
                              <div className="table-wrap">
                                <table className="table">
                                  <thead>
                                    <tr>
                                      <th>Milestone</th>
                                      <th>Planned</th>
                                      <th>This cycle</th>
                                      <th />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {c.milestones.map((m) => (
                                      <tr key={m.programmeTaskId}>
                                        <td>{m.name}</td>
                                        <td className="mono muted">{fmtDate(day(m.planned))}</td>
                                        <td>
                                          <input
                                            className="sched-input"
                                            type="date"
                                            value={dateEdit.dates[m.programmeTaskId] ?? m.due}
                                            disabled={!!m.completedAt}
                                            title={
                                              m.completedAt
                                                ? 'Signed off — reopen it before changing its date'
                                                : undefined
                                            }
                                            onChange={(e) =>
                                              setDateEdit({
                                                ...dateEdit,
                                                dates: {
                                                  ...dateEdit.dates,
                                                  [m.programmeTaskId]: e.target.value,
                                                },
                                              })
                                            }
                                          />
                                        </td>
                                        <td>
                                          {m.completedAt ? (
                                            <span className="muted">signed off</span>
                                          ) : (dateEdit.dates[m.programmeTaskId] ?? m.due) !==
                                            m.planned ? (
                                            <button
                                              className="btn btn--small"
                                              title="Back to the planned date — the override is deleted, so this milestone tracks the plan again"
                                              onClick={() =>
                                                setDateEdit({
                                                  ...dateEdit,
                                                  dates: {
                                                    ...dateEdit.dates,
                                                    [m.programmeTaskId]: m.planned,
                                                  },
                                                })
                                              }
                                            >
                                              Reset to plan
                                            </button>
                                          ) : (
                                            <span className="muted">follows the plan</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="tm-editor__actions">
                                <button
                                  className="btn btn--primary btn--small"
                                  disabled={busy}
                                  onClick={() =>
                                    act(() => sb.setCycleDates(prog.id, c.fy, dateEdit.dates)).then(
                                      (done) => {
                                        if (done) setDateEdit(null)
                                      }
                                    )
                                  }
                                >
                                  Save dates
                                </button>
                                <button className="btn btn--small" onClick={() => setDateEdit(null)}>
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}
